/**
 * DeleteOrderModal Component
 *
 * Confirmation modal for deleting/cancelling an order
 */

'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import { ordersApi } from '@/src/shared/api/orders.api';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';

interface DeleteOrderModalProps extends BaseModalProps {
  orderId?: string;
  orderTitle?: string;
  onSuccess?: () => void;
}

/**
 * Delete Order Modal - allows clients to cancel/delete their orders
 * Usage: const { open } = useModal('delete-order'); open({ orderId: '123', orderTitle: 'Task' });
 */
export function DeleteOrderModal({
  isOpen,
  onClose,
  orderId,
  orderTitle,
  onSuccess,
}: DeleteOrderModalProps) {
  const t = useTranslations('profile.orders');

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!orderId) return;

    setIsDeleting(true);

    try {
      // Update order status to CANCELLED
      await ordersApi.updateStatus(orderId, 'CANCELLED');

      toast.success(t('deleteSuccess'));
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error(t('deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      animation="zoom"
      title={t('deleteModal.title')}
      showCloseButton={!isDeleting}
      closeOnOverlayClick={!isDeleting}
      closeOnEsc={!isDeleting}
    >
      <div className="space-y-5">
        {/* Warning message */}
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <svg
            className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <Typography variant="bodySm" weight="medium" color="primary" className="text-red-900 mb-1">
              {t('deleteModal.warning')}
            </Typography>
            <Typography variant="bodySm" color="secondary" className="text-red-700">
              {t('deleteModal.description')}
            </Typography>
          </div>
        </div>

        {/* Order title */}
        {orderTitle && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <Typography variant="bodySm" color="secondary" className="mb-1">
              {t('deleteModal.orderTitle')}
            </Typography>
            <Typography variant="body" weight="medium" color="primary">
              {orderTitle}
            </Typography>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg',
              'hover:bg-gray-50 transition-colors font-medium',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            disabled={isDeleting}
          >
            {t('deleteModal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className={cn(
              'flex-1 px-4 py-3 rounded-lg font-medium transition-colors',
              'bg-red-600 text-white',
              'hover:bg-red-700',
              'disabled:bg-gray-300 disabled:cursor-not-allowed'
            )}
          >
            {isDeleting ? t('deleteModal.deleting') : t('deleteModal.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
